import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { EmSwash } from "../components/EmSwash";
import { useNoIndex } from "../lib/useNoIndex";

/**
 * Register — student sign-up (email + password + school).
 *
 * Donors don't need this page — they use Google OAuth from /login.
 */
export default function Register() {
  useNoIndex();
  const { user, loading, registerStudent, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "", display_name: "", school: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Feature gate — until student campaigns are open, real signup routes
  // to a Coming Soon page. Existing student accounts can still log in
  // through /login (this gate does NOT affect /login).
  const studentsOpen = String(process.env.REACT_APP_STUDENT_CAMPAIGNS_OPEN || "").toLowerCase() === "true";

  useEffect(() => {
    if (!studentsOpen) {
      navigate("/students-coming-soon", { replace: true });
    }
  }, [studentsOpen, navigate]);

  useEffect(() => {
    if (!loading && user) {
      const to = location.state?.from || "/dashboard";
      navigate(to, { replace: true });
    }
  }, [loading, user, navigate, location.state]);

  if (!studentsOpen) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await registerStudent({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        display_name: form.display_name.trim() || null,
        school: form.school.trim() || null,
      });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Sign-up failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="register-page" className="bg-sand">
      <Navbar
        onStart={() => navigate("/register")}
        onDonate={loginWithGoogle}
        primaryLabel="Create account"
        secondaryLabel="Sign in with Google"
      />

      <section className="mx-auto max-w-3xl px-6 pb-24 pt-32 lg:px-8 lg:pt-44">
        <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">
          Create a student account
        </span>
        <h1 className="mt-4 font-serif text-3xl lg:text-[3.5rem] leading-[0.95] tracking-tight text-ink">
          Start your <EmSwash>page.</EmSwash>
        </h1>
        <p className="mt-6 max-w-md text-base leading-relaxed text-olive">
          Sign up with your email, verify your .edu, and you can launch a campaign in about ten minutes.
        </p>

        <motion.form
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          onSubmit={handleSubmit}
          className="mt-10 grid grid-cols-1 gap-5 rounded-2xl border-2 border-tangerine bg-bone p-6 lg:p-8"
          data-testid="register-form"
        >
          <label className="block">
            <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">Full name</span>
            <input
              type="text"
              value={form.display_name}
              onChange={set("display_name")}
              data-testid="register-name-input"
              className="mt-2 w-full rounded-md border border-border bg-sand px-4 py-3 font-serif text-3xl text-ink focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10"
            />
          </label>
          <label className="block">
            <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">Email</span>
            <input
              type="email"
              required
              value={form.email}
              onChange={set("email")}
              data-testid="register-email-input"
              className="mt-2 w-full rounded-md border border-border bg-sand px-4 py-3 font-serif text-3xl text-ink focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10"
            />
          </label>
          <label className="block">
            <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">
              Password (min 8 chars)
            </span>
            <input
              type="password"
              required
              minLength={8}
              value={form.password}
              onChange={set("password")}
              data-testid="register-password-input"
              className="mt-2 w-full rounded-md border border-border bg-sand px-4 py-3 font-serif text-3xl text-ink focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10"
            />
          </label>
          <label className="block">
            <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">
              School (optional — you can add later)
            </span>
            <input
              type="text"
              value={form.school}
              onChange={set("school")}
              placeholder="e.g. Stanford University"
              data-testid="register-school-input"
              className="mt-2 w-full rounded-md border border-border bg-sand px-4 py-3 font-serif text-3xl text-ink placeholder:text-ink/30 focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10"
            />
          </label>

          {error && (
            <p role="alert" data-testid="register-error" className="text-[13px] text-scarlet">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="register-submit"
            className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-6 py-4 text-base font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-60 sm:w-auto"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Creating your account…
              </>
            ) : (
              <>Create account <ArrowRight className="h-4 w-4" /></>
            )}
          </button>

          <p className="text-[13px] text-olive">
            Already have an account?{" "}
            <Link to="/login" data-testid="register-login-link" className="font-semibold text-ink underline underline-offset-4 hover:text-tangerine">
              Sign in
            </Link>
          </p>
        </motion.form>
      </section>

      <Footer variant="minimal" />
    </main>
  );
}
